<template>
  <Transition
    name="slide-in-top"
    @mouseover="openMegaNav(megaNavMenu)"
    @mouseleave="delayedCloseMegaNav"
  >
    <div v-if="isOpen" class="mega-nav">
      <div class="grid">
        <div
          v-for="(column, columnIndex) in columns"
          :key="columnIndex"
          class="col"
          :class="{
            'col--banner': column.banner || column.carousel,
            'col--editorial-cards': column.editorialCards,
            'col--6': Number(column.span) === 6,
          }"
          :style="getColumnStyle(column.span)"
        >
          <div
            v-for="(group, groupIndex) in column.groups"
            :key="groupIndex"
            class="group"
          >
            <div v-if="group.title" class="group__heading">
              {{ group.title }}
            </div>
            <ul class="nav__list">
              <li
                v-for="(link, linkIndex) in group.links"
                :key="linkIndex"
                class="nav__item"
              >
                <Button
                  form="underline-invert"
                  tag="a"
                  :class="getLinkClass(link)"
                  :href="link.disabled ? null : link.url || null"
                  :disabled="link.disabled"
                >
                  {{ link.title }}
                </Button>
                <div class="badges">
                  <Badge
                    v-for="(badge, index) in link.badges"
                    :key="index"
                    size="small"
                    :theme="badge.theme"
                  >
                    {{ badge.text }}
                  </Badge>
                </div>
              </li>
            </ul>
          </div>
          <NavigationCarousel
            v-if="column.carousel"
            :items="column.carousel.items"
          />
          <NavigationEditorialCards
            v-else-if="column.editorialCards"
            :items="column.editorialCards.items"
          />
          <NavigationBanner
            v-else-if="column.banner"
            cover
            :orientation="column.banner.orientation"
            :images="column.banner.images"
            :text="column.banner.text"
            :url="column.banner.url"
            :theme="column.banner.theme"
            :align-image-bottom-desktop="
              column.banner.alignImageBottomDesktop === true
            "
          />
        </div>
      </div>
    </div>
  </Transition>
  <Transition name="fade">
    <div v-if="isOpen" class="blur"></div>
  </Transition>
</template>

<script>
import Button from "@/components/ButtonV2.vue";
import NavigationBanner from "@/components/navigation/NavigationBanner.vue";
import NavigationCarousel from "@/components/navigation/NavigationCarousel.vue";
import NavigationEditorialCards from "@/components/navigation/NavigationEditorialCards.vue";
import { mapState, mapActions } from "vuex";
// import navigationContent from "@/content/navigation.json";
import Badge from "@/components/BadgeV2.vue";

export default {
  name: "DesktopMegaNav",
  components: {
    Button,
    NavigationBanner,
    NavigationCarousel,
    NavigationEditorialCards,
    Badge,
  },
  computed: {
    ...mapState("app", ["isMegaNavOpen", "megaNavMenu", "navVersion"]),
    columns() {
      const matchedNav = this.navigation.items.find((item) => {
        return item.slug === this.megaNavMenu;
      });

      return matchedNav?.mega?.columns;
    },
    isOpen() {
      return this.isMegaNavOpen && this.columns;
    },
    navigation() {
      try {
        return require(`@/content/navigationV${this.navVersion}.json`);
      } catch (e) {
        console.error(e);
        return require(`@/content/navigation.json`);
      }
    },
  },
  methods: {
    ...mapActions("app", ["openMegaNav", "delayedCloseMegaNav"]),
    /**
     * Maps JSON span values (12-col scale, halves allowed) to a 24-col grid.
     * e.g. span 3 → 6 tracks, span 3.5 → 7 tracks, span 6 → 12 tracks.
     */
    getColumnStyle(span) {
      if (span == null || span === "") {
        return {};
      }

      const gridSpan = Math.round(Number(span) * 2);

      if (!Number.isFinite(gridSpan) || gridSpan < 1 || gridSpan > 24) {
        return {};
      }

      return { gridColumn: `span ${gridSpan}` };
    },
    getLinkClass(navItem) {
      const baseClass = "nav__link";
      const classes = [baseClass];
      if (navItem.modifier) {
        classes.push(`${baseClass}--${navItem.modifier}`);
      }
      // Support simple boolean italic flag from JSON
      if (navItem.italic) {
        classes.push(`${baseClass}--italic`);
      }
      // Support red highlight for child links via JSON flags
      if (navItem.highlight === true || navItem.highlightRed === true) {
        classes.push(`${baseClass}--highlight`);
      }
      if (navItem.disabled) {
        classes.push(`${baseClass}--disabled`);
      }
      return classes.join(" ");
    },
  },
};
</script>

<style lang="scss" scoped>
.mega-nav {
  background-color: $color-white;
  position: relative;
  z-index: 4;
}

.blur {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 3;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur($blur-amount);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-in-top-enter-active,
.slide-in-top-leave-active {
  transition: transform 0.25s ease-out, opacity 0.25s ease-out;
  opacity: 1;
}

.slide-in-top-enter-from,
.slide-in-top-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}

.grid {
  display: grid;
  padding: 30px 40px 30px;
  grid-template-columns: repeat(24, 1fr);
  gap: 20px;
  align-items: start;
}

.col {
  min-width: 0;
}

.col--editorial-cards {
  display: flex;
  justify-content: flex-end;
  min-width: 0;
}

.col--banner {
  display: flex;
  justify-content: flex-end;
  min-width: 0;

  &:not(.col--6) {
    justify-content: stretch;

    :deep(.navigation-banner) {
      width: 100%;
    }

    :deep(.navigation-banner--landscape .wrapper),
    :deep(.navigation-banner--portrait .wrapper) {
      width: 100%;
      height: 282px;
      margin-bottom: 20px;
    }
  }

  &.col--6 {
    :deep(.navigation-banner--landscape .wrapper) {
      width: 552px;
      height: 282px;
      margin-bottom: 20px;
    }

    :deep(.navigation-banner--portrait .wrapper) {
      width: 310px;
      height: 282px;
      margin-bottom: 20px;
    }
  }
}

.nav__list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.group {
  margin-bottom: 0;
}

.group__heading {
  @include utility-body(14px);
  color: $color-grey-dark;
  text-transform: uppercase;
  margin-bottom: 10px;
}

.badges {
  position: relative;
  top: -5px;
  display: inline-block;
  :deep(.badge) {
    background-color: transparent !important;
    color: $color-red;
    padding: 0 4px;
  }
}

.nav__link {
  @include editorial-body(14px);
  text-transform: none;
  display: inline-block;
  &--highlight {
    color: $color-red;
  }
  &--italic {
    font-style: italic !important;
  }
  &--disabled {
    pointer-events: none;
    opacity: 0.5;
    cursor: not-allowed;
  }
}

:deep(.nav__link.button--underline-invert) {
  @include editorial-body(14px);
  text-transform: none;
}

:deep(.navigation-banner .wrapper .media) {
  height: 100%;
}
</style>

<style lang="scss">
.header-wrapper {
  .header,
  .banner {
    position: relative;
    z-index: 5;
  }
}
</style>
